import React, { useState, useEffect } from "react";
import FormLogin from "../components/login/FormLogin";
import ApiCall from "../constants/Api";
import { helpAxios } from "../helpers/helpAxios";
import { errorHandler } from "../components/ErrorHandler";
import Dashboard from "./Dashboard";
import SiteConfig from "../constants/SiteConfig";
import { WebSocketProvider } from "../services/WebSocketProvider";
import ChangeUserPassword from "../components/utilities/ChangeUserPassword";
import { successMsg } from "../components/SuccessHandler";
import Swal from "sweetalert2";
import MessagesConfig from "../constants/MessagesConfig";
import {
  useUpdateFunctions,
  useSelectFunctions,
  useSpecialFunctions,
} from "../functions";

const Login = () => {
  let loginInit = JSON.parse(localStorage.getItem("loginInfo")) || {
    LogonSessionID: "",
  };
  const [loginInfo, setLoginInfo] = useState(loginInit);
  let loginUUID = localStorage.getItem("UUID") || "";
  const [loginInfoUUID, setLoginInfoUUID] = useState(loginUUID);
  const [loading, setLoading] = useState(false);
  let publicIPInit = localStorage.getItem("publicIP") || {
    publicIPInit: {},
  };
  const [publicIP, setPublicIP] = useState(publicIPInit);
  const [pingStatus, setPingStatus] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [dialogPingVisible, setDialogPingVisible] = useState(false);
  const [change, setChange] = useState(false);
  const [changePasswordStatus, setChangePasswordStatus] = useState(false);
  const [newUserPassword, setNewUserPassword] = useState({
    user: "",
    password: "",
  });
  const { resetUserPassword } = useUpdateFunctions();
  const { getMyIP } = useSelectFunctions();
  const { loginUser, logoutUser } = useSpecialFunctions();

  // Function to obtain server time
  const getTimeServer = (time) => {
    var timeServer = new Date(time);
    return (
      ("0" + timeServer.getHours()).slice(-2) +
      ":" +
      ("0" + timeServer.getMinutes()).slice(-2) +
      ":" +
      ("0" + timeServer.getSeconds()).slice(-2)
    );
  };

  useEffect(() => {
    if (changePasswordStatus) {
      successMsg("Password has been changed successfully");
      setChangePasswordStatus(false);
    }
  }, [changePasswordStatus]);

  const onlineResetUserPassword = async (req) => {
    let res;
    try {
      res = await resetUserPassword(req);

      // Show error message
      if (res.OutParams.Result !== 0) {
        errorHandler(res.error);
      }
    } catch (error) {
      errorHandler(error.toString());
    }
    return res;
  };

  // Function for consuming the LOGIN USER method
  const userLogin = (form) => {
    setDialogPingVisible(true);
    setPingStatus(false);

    const fetchData = async () => {
      const isPingSuccessful = await validatePing();
      if (!isPingSuccessful) {
        setPingStatus(false);
        setLoginInfo({ LogonSessionID: "" });
        setLoginInfoUUID("");
      } else {
        setPingStatus(true);

        const resIP = await getMyIP();
        if (!resIP.error) {
          setPublicIP(resIP.IP);

          const { userName, password } = form;

          const res = await loginUser({
            LoginName: userName,
            Password: password,
            PhoneLine: SiteConfig.LOGIN_PHONE_LINE,
            IP: resIP.IP,
            System: SiteConfig.LOGIN_SYSTEM_NAME,
          });

          if (res.error) {
            if (pingStatus) {
              setDialogPingVisible(false);
            }

            if (res.data.ResetPassword) {
              ChangeUserPassword(
                userName,
                password,
                onlineResetUserPassword,
                setChangePasswordStatus,
                setNewUserPassword,
              );
            }
          } else {
            setLoginInfo({ ...res, LogonSessionID: res.UUID });
            setLoginInfoUUID(res.UUID);
            setPingStatus(true);
            localStorage.setItem("publicIP", resIP.IP);
            localStorage.setItem("userName", userName);
            localStorage.setItem("UserRights", JSON.stringify(res.UserRights));
          }
        }
      }
    };

    fetchData();
  };

  // Function for consuming the LOGOUT USER method
  const userLogout = () => {
    setDialogPingVisible(false);
    const fetchData = async () => {
      const res = await logoutUser(loginInfo.LogonSessionID);

      if (res.error) {
        let errorDet;
        if (res.errorDetails) {
          errorDet = res.errorDetails;
        }
        errorHandler("LOGOUT USER: " + res.error, errorDet);
        setLoginInfo({ LogonSessionID: "" });
        setLoginInfoUUID("");
        localStorage.clear();
      } else {
        setLoginInfo({ LogonSessionID: "" });
        setLoginInfoUUID("");
        localStorage.clear();
      }
    };

    fetchData();
  };

  useEffect(() => {
    localStorage.setItem("loginInfo", JSON.stringify(loginInfo));
  }, [loginInfo]);

  useEffect(() => {
    if (change) {
      if (localStorage.getItem("UUID")) {
        setPingStatus(true);
      } else {
        setPingStatus(false);
        setLoginInfo({ LogonSessionID: "" });
        setLoginInfoUUID("");
        localStorage.clear();
      }
      setIsLoading(false);
      setChange(false);
    }
  }, [change]);

  useEffect(() => {
    //This handler if for the opened tab directed from the portal
    const handleMessage = (e) => {
      const { data } = e;
      if (
        data?.type === MessagesConfig.SessionMessageTypes.REQUEST_SESSION_STATUS
      ) {
        e.source?.postMessage(
          {
            type: MessagesConfig.SessionMessageTypes.SESSION_STATUS,
            payload: { status: MessagesConfig.MessageStatus.OK },
          },
          e.origin,
        );
        return;
      }
      if (data?.type === MessagesConfig.SessionMessageTypes.SESSION_INIT) {
        setPingStatus(true);
        setLoginInfo({
          UUID: data.payload.UUID,
          LogonSessionID: data.payload.UUID,
        });
        setLoginInfoUUID(data.payload.UUID);
        setPingStatus(true);
        localStorage.setItem("UUID", data.payload.UUID);
        localStorage.setItem("userName", data.payload.User);
        localStorage.setItem(
          "UserRights",
          JSON.stringify(data.payload.UserRights),
        );
        if (Swal.isVisible()) {
          Swal.close();
        }
      }
    };
    //This handler is for the already opened tabs, to update the UUID
    const handleStorageChange = async (event) => {
      if (event.key === "UUID") {
        if (event.newValue === "") {
          setPingStatus(false);
          setDialogPingVisible(false);
        } else {
          setLoginInfoUUID(event.newValue);
          setPingStatus(true);
          setLoginInfo({
            OutParams: {
              Result: 0,
              ErrParams: "",
              ErrMsg: "",
            },
            UUID: event.newValue,
            LogonSessionID: event.newValue,
          });
        }
      }
    };

    setChange(true);

    window.addEventListener("storage", handleStorageChange);
    window.addEventListener("message", handleMessage);

    return () => {
      window.removeEventListener("storage", handleStorageChange);
      window.removeEventListener("message", handleMessage);
    };
  }, []);

  useEffect(() => {
    localStorage.setItem("UUID", loginInfoUUID);
  }, [loginInfoUUID]);

  //Service Ping
  const Ping = async (methodName, serviceName, isProxy) => {
    const response = isProxy
      ? await helpAxios({}, methodName)
      : await helpAxios({}, ApiCall.INVOKE_GET_METHOD, {
          MethodName: methodName,
          ServiceName: serviceName,
        });

    if (!response) return;
    return response;
  };

  //Validate services
  const validatePing = async () => {
    try {
      //PROXY PING
      const resProxySec = await Ping(ApiCall.PING, "", true);
      if (resProxySec && resProxySec.error) {
        const resLegacy = await Ping(ApiCall.PING_LEGACY, "", true);
        if (resLegacy && resLegacy.error) {
          return false;
        }
      }
      //RACING MANAGER PING
      const resPingRacingMgmt = await Ping(
        ApiCall.PING,
        SiteConfig.RACING_MGMT_SVC,
      );
      if (!resPingRacingMgmt || resPingRacingMgmt.error) {
        const resLegacy = await Ping(
          ApiCall.PING_LEGACY,
          SiteConfig.RACING_MGMT_SVC,
        );
        if (!resLegacy || resLegacy.error) {
          return false;
        }
      }

      //USER ACCESS PING
      const resPingUserAccess = await Ping(
        ApiCall.PING,
        SiteConfig.USER_ACCESS_SVC,
      );
      if (!resPingUserAccess || resPingUserAccess.error) {
        const resLegacy = await Ping(
          ApiCall.PING_LEGACY,
          SiteConfig.USER_ACCESS_SVC,
        );
        if (!resLegacy || resLegacy.error) {
          return false;
        }
      }

      //RACING INTEGRATION PING
      const resPingRacingIntegration = await Ping(
        ApiCall.PING,
        SiteConfig.RACING_INTEGRATION_SVC,
      );
      if (!resPingRacingIntegration || resPingRacingIntegration.error) {
        const resLegacy = await Ping(
          ApiCall.PING_LEGACY,
          SiteConfig.RACING_INTEGRATION_SVC,
        );
        if (!resLegacy || resLegacy.error) {
          return false;
        }
      }

      //RACING SCORING PING
      const resPingRacingScoring = await Ping(
        ApiCall.PING,
        SiteConfig.RACING_SCORING_SVC,
      );
      if (!resPingRacingScoring || resPingRacingScoring.error) {
        const resLegacy = await Ping(
          ApiCall.PING_LEGACY,
          SiteConfig.RACING_SCORING_SVC,
        );
        if (!resLegacy || resLegacy.error) {
          return false;
        }
      }

      //RACING TICKET VALIDATOR PING
      const resPingRacingTicketValidator = await Ping(
        ApiCall.PING,
        SiteConfig.RACING_TICKET_VALIDATOR_SVC,
      );
      if (!resPingRacingTicketValidator || resPingRacingTicketValidator.error) {
        const resLegacy = await Ping(
          ApiCall.PING_LEGACY,
          SiteConfig.RACING_TICKET_VALIDATOR_SVC,
        );
        if (!resLegacy || resLegacy.error) {
          return false;
        }
      }

      //LOG PING
      const resLogs = await Ping(ApiCall.PING, SiteConfig.LOG_SVC);
      if (!resLogs || resLogs.error) {
        const resLegacy = await Ping(ApiCall.PING_LEGACY, SiteConfig.LOG_SVC);
        if (!resLegacy || resLegacy.error) {
          return false;
        }
      }

      return true;
    } catch (error) {
      return false;
    }
  };

  return (
    <>
      {isLoading ? (
        <div></div>
      ) : (
        <>
          {!pingStatus || !loginInfoUUID ? (
            <>
              <div className="" style={{ backgroundColor: "#f4f7fa" }}>
                <FormLogin
                  loginCall={userLogin}
                  setLoginInfo={setLoginInfo}
                  pingStatus={pingStatus}
                  dialogPingVisible={dialogPingVisible}
                  setDialogPingVisible={setDialogPingVisible}
                  newUserPassword={newUserPassword}
                />
              </div>
            </>
          ) : (
            <WebSocketProvider>
              <Dashboard
                data={loginInfo}
                logout={userLogout}
                getTimeServer={getTimeServer}
                loading={loading}
                setLoading={setLoading}
                setLoginInfo={setLoginInfo}
                setLoginInfoUUID={setLoginInfoUUID}
                publicIP={publicIP}
              />
            </WebSocketProvider>
          )}
        </>
      )}
    </>
  );
};

export default Login;
